import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { Box, Grid } from "@mui/system";
import { IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import ConversionsUpload from "./BeAwareTabs/ConversionsUpload/ConversionsUpload";
import FraudPlayers, {
  BeAwareTableRef,
} from "./BeAwareTabs/FraudPlayers/FraudPlayers";
import NegativeBalance from "./BeAwareTabs/NegativeBalance/NegativeBalance";
import {
  selectBizBeAwareConversionsUploadLoading,
  selectBizBeAwareFraudPlayersLoading,
  selectBizBeAwareNegativeBalanceLoading,
} from "../../../../../features/bizDevelopmentDashboardSlice";
import FloatingActionItems from "../../../../../pages/FloatingItems/FloatingActionItems";
import { useBizDevelopmentDashboard } from "../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboard";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

// URL slugs for each subtab, indexed by tab position. Persisted in the
// `beAwareTab` query param so a refresh keeps the user on the same subtab.
const TAB_SLUGS = ["fraud-players", "negative-balance", "conversions-upload"];

const BeAwareTabs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = TAB_SLUGS.indexOf(searchParams.get("beAwareTab") ?? "");
  const [value, setValue] = React.useState(tabFromUrl === -1 ? 0 : tabFromUrl);
  const {
    beAwareFraudPlayersOutput,
    beAwareNegativeBalanceOutput,
    beAwareConversionsUploadOutput,
  } = useSelector((state: any) => state.bizDevelopmentDashboard);
  // Output of the currently active subtab, used to disable the floating action
  // button only when the visible subtab has no data.
  const activeSubtabOutput =
    value === 0
      ? beAwareFraudPlayersOutput
      : value === 1
        ? beAwareNegativeBalanceOutput
        : beAwareConversionsUploadOutput;
  const fraudPlayersRef = React.useRef<BeAwareTableRef>(null);
  const negativeBalanceRef = React.useRef<BeAwareTableRef>(null);
  const conversionsUploadRef = React.useRef<BeAwareTableRef>(null);
  const isFraudPlayersLoading = useSelector(
    selectBizBeAwareFraudPlayersLoading,
  );
  const isNegativeBalanceLoading = useSelector(
    selectBizBeAwareNegativeBalanceLoading,
  );
  const isConversionsUploadLoading = useSelector(
    selectBizBeAwareConversionsUploadLoading,
  );
  const isRefreshDisabled =
    value === 0
      ? isFraudPlayersLoading
      : value === 1
        ? isNegativeBalanceLoading
        : isConversionsUploadLoading;

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("beAwareTab", TAB_SLUGS[newValue]);
        next.delete("fraudPlayersPage");
        next.delete("negativeBalancePage");
        next.delete("conversionsUploadPage");
        return next;
      },
      { replace: true },
    );
  };

  const handleRefresh = () => {
    if (value === 0) {
      fraudPlayersRef.current?.refresh();
      return;
    }
    if (value === 1) {
      negativeBalanceRef.current?.refresh();
      return;
    }
    conversionsUploadRef.current?.refresh();
  };
  const { getSitesFromParams } = useBizDevelopmentDashboard();

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Grid container spacing={2} alignItems="center" p={2}>
          <Grid size={6}>
            <Box display={"flex"} alignItems={"center"}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="basic tabs example"
                sx={{
                  background: "#F5F5F5",
                  borderRadius: "8px",
                  padding: "4px",
                  minHeight: "30px",
                  "& .MuiTab-root": {
                    color: "#00000061",
                    padding: "5px 16px",
                    minHeight: "30px",
                    "&.Mui-selected": {
                      color: "#000000DE",
                      background: "#ffffff",
                      borderRadius: "4px",
                    },
                  },

                  /* Active line color */
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#9C27B0",
                    height: "0px", // optional
                    borderRadius: "10px", // optional
                  },
                }}
              >
                <Tab
                  data-tracker="853"
                  label="Fraud Players"
                  {...a11yProps(0)}
                />
                <Tab
                  data-tracker="854"
                  label="Negative Balance"
                  {...a11yProps(1)}
                />
                <Tab
                  data-tracker="855"
                  label="Conversions Upload"
                  {...a11yProps(2)}
                />
              </Tabs>
            </Box>
          </Grid>
          <Grid size={6}>
            <Box display={"flex"} justifyContent={"flex-end"}>
              <IconButton
                aria-label="refresh"
                color="secondary"
                disabled={isRefreshDisabled}
                onClick={handleRefresh}
                data-tracker="856"
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <CustomTabPanel value={value} index={0}>
          <FraudPlayers ref={fraudPlayersRef} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <NegativeBalance ref={negativeBalanceRef} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          <ConversionsUpload ref={conversionsUploadRef} />
        </CustomTabPanel>
        <div data-tracker="858">
          <FloatingActionItems
            selectedSiteFloatingAction={getSitesFromParams().join(",")}
            selectedDimensionsFloatingAction=""
            trackIdFloatingAction="859"
            disabledButton={activeSubtabOutput?.length === 0}
          />
        </div>
      </Box>
    </>
  );
};

export default BeAwareTabs;
